<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { Camera, CheckCircle2, Clock3, LocateFixed, Navigation, Share2, ShieldCheck, Truck } from "lucide-vue-next";
import { api, ApiError } from "@/lib/api";
import { session } from "@/lib/session";
import type { Journey, Task } from "@/types";

const route = useRoute(); const router = useRouter();
const task = ref<Task | null>(null); const loading = ref(true); const claiming = ref(false);
const error = ref(""); const toast = ref("");
const routePoints = computed(() => task.value ? [task.value.originName, ...task.value.waypoints.map((item) => item.name), task.value.destinationName] : []);

async function load() {
  try { task.value = await api.get<Task>(`/tasks/${route.params.id}`); }
  catch (cause) { error.value = cause instanceof Error ? cause.message : "任务加载失败"; }
  finally { loading.value = false; }
}

async function claim() {
  claiming.value = true; error.value = "";
  try {
    const journey = await api.post<Journey>(`/tasks/${route.params.id}/claim`, {}, session.driverToken);
    await router.push(`/journeys/${journey.id}`);
  } catch (cause) {
    error.value = cause instanceof ApiError ? cause.message : "领取失败";
    if (cause instanceof ApiError && cause.code === "PROFILE_REQUIRED") setTimeout(() => router.push("/profile"), 1000);
  } finally { claiming.value = false; }
}

async function share() {
  const data = { title: task.value?.title || "HaulGauge 能耗任务", text: `奖励 ¥${((task.value?.rewardCents || 0) / 100).toFixed(0)}，顺路完成货车能耗实测`, url: location.href };
  try {
    if (navigator.share) await navigator.share(data); else { await navigator.clipboard.writeText(location.href); toast.value = "任务链接已复制"; setTimeout(() => toast.value = "", 1800); }
  } catch { /* user cancelled */ }
}

onMounted(load);
</script>

<template>
  <div v-if="loading" class="card state-box">加载任务详情…</div>
  <section v-else-if="task" class="stack">
    <div class="card">
      <div class="row between"><span class="pill" :class="task.routeType === 'national' ? 'pill-success' : ''">{{ task.routeType === "highway" ? "高速任务" : "国道路段" }}</span><button class="icon-button" aria-label="分享" @click="share"><Share2 :size="20" /></button></div>
      <h2 style="margin:10px 0 5px">{{ task.title }}</h2>
      <div class="small muted">{{ task.code }} · {{ task.vehicleRequirement }}</div>
      <div class="detail-stats">
        <div><strong>{{ task.distanceKm }}</strong><span>路线公里</span></div>
        <div><strong class="money">{{ (task.rewardCents / 100).toFixed(0) }}</strong><span>完成奖励</span></div>
        <div><strong>{{ task.remainingSlots }}</strong><span>剩余名额</span></div>
      </div>
    </div>
    <div class="card">
      <h3 class="section-title"><Navigation :size="18" />指定路线</h3>
      <div class="timeline">
        <div v-for="(point,index) in routePoints" :key="point" class="timeline-item">
          <span :class="['timeline-dot', index === routePoints.length - 1 ? 'end' : '']">{{ index + 1 }}</span>
          <div><b>{{ point }}</b><div class="small muted">{{ index === 0 ? "起点打卡" : index === routePoints.length - 1 ? "终点打卡并提交" : "途径点必须双照片打卡" }}</div></div>
        </div>
      </div>
    </div>
    <div class="card">
      <h3 class="section-title"><ShieldCheck :size="18" />完成要求</h3>
      <ul class="rule-list">
        <li><LocateFixed :size="17" />进入打卡点 {{ task.geofenceRadiusM }} 米范围后按钮才解锁</li>
        <li><Camera :size="17" />每个点实时拍仪表盘、车头道路各 1 张</li>
        <li><Clock3 :size="17" />24 小时内完成，累计停留不超过 20 分钟</li>
        <li><Truck :size="17" />全程不补充燃油/LNG，按正常运输习惯驾驶</li>
        <li><CheckCircle2 :size="17" />轨迹重合度 ≥80%；70%–80% 进入人工复核</li>
      </ul>
      <p class="info-banner">{{ task.rulesText }}</p>
      <RouterLink to="/rules" class="small" style="color:var(--primary)">查看完整参与规则与常见问题 ›</RouterLink>
    </div>
    <p v-if="error" class="error-banner">{{ error }}</p>
    <button class="btn btn-primary btn-block" :disabled="claiming || task.remainingSlots <= 0" @click="claim">{{ claiming ? "正在领取…" : "确认领取任务" }}</button>
  </section>
  <div v-else class="card state-box">{{ error || "任务不存在" }}</div>
  <div v-if="toast" class="toast">{{ toast }}</div>
</template>

<style scoped>
.detail-stats { margin-top:20px; padding-top:16px; display:grid; grid-template-columns:repeat(3,1fr); border-top:1px solid var(--line); text-align:center; }
.detail-stats div { display:grid; gap:4px; }
.detail-stats div + div { border-left:1px solid var(--line); }
.detail-stats strong { font-size:20px; }
.detail-stats span { color:var(--muted); font-size:11px; }
.section-title { margin:0 0 15px; display:flex; align-items:center; gap:7px; font-size:15px; }
.timeline { display:grid; gap:0; }
.timeline-item { position:relative; min-height:60px; display:grid; grid-template-columns:32px 1fr; gap:9px; }
.timeline-item:not(:last-child)::after { content:""; position:absolute; left:13px; top:28px; bottom:1px; border-left:2px dashed #cdd4df; }
.timeline-dot { z-index:1; width:28px; height:28px; display:grid; place-items:center; border-radius:50%; color:#fff; background:var(--success); font-size:11px; font-weight:800; }
.timeline-dot.end { background:var(--warning); }
.rule-list { margin:0 0 14px; padding:0; display:grid; gap:12px; list-style:none; color:#515d71; font-size:13px; }
.rule-list li { display:flex; align-items:flex-start; gap:9px; line-height:1.55; }
.rule-list svg { flex:none; color:var(--primary); margin-top:2px; }
</style>
